import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useRouteParams } from "@vueuse/router";
import { useMetadataExperienceApi } from "@/composables/useMetadataExperienceApi";
import {
  useCurrentBookingInformationQuery,
  BOOKING_INFORMATION_QUERY_KEY,
} from "@/features/experience-curation/queries/useBookingInformationQuery";
import { useCurationEmergencyContact } from "@/features/experience-curation/sections/customer-info/emergency-contact/useCurationEmergencyContact";
import { useCurationVoucherType } from "@/features/experience-curation/sections/customer-info/voucher-type/useCurationVoucherType";
import type { ExperienceBookingInformation, BookingInformation } from "@/types/generated/MetadataExperiencesApi";

const DEFAULT_VOUCHER_TYPE: BookingInformation["voucher_type"] = "MOBILE";

/**
 * Mutation responsible for persisting booking information (voucher type + emergency contact).
 * Reads directly from the field stores — no syncToLegacyStore needed.
 */
export function useCurationBookingInfoSaveMutation() {
  const experienceId = useRouteParams<string>("id");
  const queryClient = useQueryClient();
  const { updateBookingInfo, createBookingInfo } = useMetadataExperienceApi();

  const bookingInfoQuery = useCurrentBookingInformationQuery();
  const voucherTypeStore = useCurationVoucherType();
  const emergencyContactStore = useCurationEmergencyContact();

  return useMutation({
    mutationFn: async () => {
      const voucherType = (voucherTypeStore.model as BookingInformation["voucher_type"]) || DEFAULT_VOUCHER_TYPE;
      const emergencyContact = emergencyContactStore.model;

      const payload: ExperienceBookingInformation = {
        experience_id: experienceId.value,
        booking_information: {
          voucher_type: voucherType,
          emergency_contact_number: emergencyContact,
        },
      };

      const existingId = bookingInfoQuery.data.value?.id;

      if (existingId) {
        await updateBookingInfo(existingId, payload);
      } else {
        await createBookingInfo(payload);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [BOOKING_INFORMATION_QUERY_KEY] });
    },
  });
}
